// src/hooks/useReduxChat.js
import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addMessage as addMessageAction,
  updateMessage as updateMessageAction,
  deleteMessage as deleteMessageAction,
  deleteChat,
  setSelectedChat,
  clearAllChat,
} from "../store/features/message/messageActions";
import { initializeChatsFromDB } from "../store/features/message/messageThunks";
import {
  selectAllChats,
  selectRecentChatIds,
  selectCurrentChatMessages,
  selectSelectedChatId,
} from "../store/features/message/messageSelectors";
import { getChatManager } from "../store/middleware/chatManagerMiddleware";
export const MAX_CHAT_STORE = 2;

export const useReduxChat = () => {
  const dispatch = useDispatch();
  const initRef = useRef(false);

  // Selectors
  const allChats = useSelector(selectAllChats);
  const recentChatIds = useSelector(selectRecentChatIds);
  const selectedChatId = useSelector(selectSelectedChatId);
  const currentMessages = useSelector(selectCurrentChatMessages);

  // Initialize from DB on mount
  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;
      dispatch(initializeChatsFromDB());
    }
  }, [dispatch]);

  // Get ChatManager instance for createMessage
  const chatManager = getChatManager();

  // Create message using ChatManager format
  const createMessage = useCallback(
    ({ content, userData, chatId, overrides }) => {
      return chatManager.createMessage({
        content,
        userData,
        chatId,
        overrides,
      });
    },
    [chatManager]
  );

  // Action creators wrapped in dispatch
  const addMessage = useCallback(
    (message) => {
      dispatch(addMessageAction(message));
    },
    [dispatch]
  );

  const updateMessage = useCallback(
    (chatId, messageId, updates) => {
      dispatch(updateMessageAction(chatId, messageId, updates));
    },
    [dispatch]
  );

  const deleteMessage = useCallback(
    (chatId, messageId) => {
      dispatch(deleteMessageAction(chatId, messageId));
    },
    [dispatch]
  );

  const selectChat = useCallback(
    (chatId) => {
      dispatch(setSelectedChat(chatId));
    },
    [dispatch]
  );

  const clearAllChats = useCallback((chatId) => {
    dispatch(clearAllChat(chatId));
  }, [dispatch]);

  const deleteChatById = useCallback(
    (chatId) => {
      dispatch(deleteChat(chatId));
    },
    [dispatch]
  );

  // Convert Redux format to ChatPage expected format
  const chatList = allChats.map((chat) => ({
    chatId: chat.chatId,
    chatMessages: chat.chatList || [],
  }));

  return {
    // State
    allChats,
    chatList, // Compatible with ChatPage
    recentChatIds,
    selectedChatId,
    currentMessages,
    maxChatLimit: MAX_CHAT_STORE,

    // Actions
    createMessage,
    addMessage,
    updateMessage,
    deleteMessage,
    selectChat,
    clearAllChats,
    deleteChatById,
    // Aliases for compatibility
    sendMessage: addMessage,
  };
};
